'use client';
import React from 'react';
import { PlusCircle } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { BlockProps } from './types';
import { isBlockEmptyForProduct } from '../blockHelpers';
import { ImageSlotContext, useImageSlot } from '../ImageSlotContext';
import { narrowSlot, rowColumnFraction } from '../imageSlot';

export function RowBlock({
  block,
  currentStyles,
  viewport,
  isAdmin,
  isBuilderCanvas,
  selectedBlockId,
  onSelectBlock,
  product,
  category,
  onUpdateBlockStyle,
  onUpdateBlockContent,
  depth = 0,
  onDuplicateBlock,
  onMoveBlockUp,
  onMoveBlockDown,
  onRemoveBlock,
  initialBlockData,
  authMode,
  isFirstBlock,
  firstBlockBreakpoints
}: BlockProps) {
  const { BlockRenderer } = require('../BlockRenderer');
  const parentImageSlot = useImageSlot();

  const configuredColumns = block.settings?.columns || 2;
  const shouldFilterChild = (child: any) => {
    if (isAdmin || !product) return false;
    if (child.type === 'product-spec' && block.settings?.collapseMissingProductSpec === true) {
      return !Array.isArray(product.specifications) || product.specifications.length === 0;
    }
    return isBlockEmptyForProduct(child, product);
  };
  const rowChildren = (block.children || []).filter((child: any) => !shouldFilterChild(child));

  if (!isAdmin && rowChildren.length === 0) {
    return null;
  }

  const columns = Math.max(1, configuredColumns - ((block.children?.length || 0) - rowChildren.length));
  const mobileOrder: Record<number, number> = block.settings?.mobileOrder || {};

  // Custom per-column widths (%) — only used when the array matches the current
  // column count; otherwise columns stay equal (repeat(N, 1fr)), same as before.
  const columnWidths: number[] | undefined =
    Array.isArray(block.settings?.columnWidths) && block.settings.columnWidths.length === columns
      ? block.settings.columnWidths
      : undefined;
  const gridTemplateFromWidths = columnWidths
    ? columnWidths.map((w: number) => `${w}%`).join(' ')
    : `repeat(${columns}, 1fr)`;
  const rowClass = `row-${block.id}`;

  const mobileColumns = block.settings?.mobileColumns !== undefined ? Number(block.settings.mobileColumns) : 1;
  const mobileLayout = block.settings?.mobileLayout || (mobileColumns === 2 ? 'two-columns' : 'stack');
  const compactHeader = columns >= 3 && mobileLayout === 'header-compact';
  const threeColumnHeader = columns >= 3 && mobileLayout === 'three-columns';
  const centeredMobileHeader = columns >= 3 && mobileLayout === 'header-centered';
  const splitMobileHeader = columns >= 3 && mobileLayout === 'header-split';
  const customThreeColumnMobile = threeColumnHeader || centeredMobileHeader || splitMobileHeader;
  const centerMiddleColumn = columns === 3 && block.settings?.centerMiddleColumn === true;
  // How wide one column renders per breakpoint — lets images inside request a fitting size.
  const columnImageSlot = narrowSlot(parentImageSlot, rowColumnFraction({
    columns,
    mobileColumns,
    mobileThreeUp: customThreeColumnMobile,
    columnWidths,
  }));

  // Admin: JS-driven viewport simulation (canvas is fixed-width, not real media queries)
  // Storefront: CSS-driven via Tailwind responsive classes (real browser viewport)
  const adminColumns =
    isAdmin
      ? viewport === 'mobile'
        ? Math.min(columns, mobileColumns)
        : viewport === 'tablet'
        // Real CSS reveals the full column count at the md breakpoint (768px, exactly
        // the tablet canvas width) via `md:grid-cols-3` when columns===3; the lg
        // breakpoint (1024px) that gates columns===4 hasn't been reached yet there.
        ? (columns === 3 ? columns : Math.min(columns, 2))
        : columns
      : columns;

  // Custom widths only make sense when every column is actually shown (desktop,
  // or tablet/mobile simulations that haven't collapsed the column count) —
  // otherwise fall back to equal columns for the simulated count, same as before.
  const adminGridTemplateColumns = isAdmin && viewport === 'mobile' && compactHeader
    ? 'minmax(0, 1fr) auto'
    : isAdmin && viewport === 'mobile' && centeredMobileHeader
    ? 'minmax(0, 1fr) auto minmax(0, 1fr)'
    : isAdmin && viewport === 'mobile' && splitMobileHeader
    ? 'minmax(0, 1fr) auto auto'
    : isAdmin && viewport === 'mobile' && threeColumnHeader
    ? 'repeat(3, minmax(0, 1fr))'
    : isAdmin && viewport !== 'mobile' && centerMiddleColumn
    ? 'minmax(0, 1fr) auto minmax(0, 1fr)'
    : adminColumns === columns && columnWidths ? gridTemplateFromWidths : `repeat(${adminColumns}, 1fr)`;

  const storefrontGridClass = !isAdmin
    ? customThreeColumnMobile
      ? columns === 3
        ? 'grid-cols-3'
        : 'grid-cols-3 lg:grid-cols-4'
      : mobileColumns === 2
      ? columns === 2
        ? 'grid-cols-2'
        : columns === 3
        ? 'grid-cols-2 md:grid-cols-3'
        : columns === 4
        ? 'grid-cols-2 lg:grid-cols-4'
        : 'grid-cols-2'
      : columns === 1
      ? 'grid-cols-1'
      : columnWidths
      ? 'grid-cols-1 md:grid-cols-none' // widths applied via injected <style> below
      : columns === 2
      ? 'grid-cols-1 md:grid-cols-2'
      : columns === 3
      ? 'grid-cols-1 md:grid-cols-3'
      : columns === 4
      ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
      : 'grid-cols-1 md:grid-cols-2'
    : '';

  const storefrontWidthCSS =
    !isAdmin && centerMiddleColumn
      ? `@media (min-width: 768px) { .${rowClass} { grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr) !important; } }`
      : !isAdmin && columnWidths && columns > 1
      ? `@media (min-width: 768px) { .${rowClass} { grid-template-columns: ${gridTemplateFromWidths} !important; } }`
      : '';

  // Header-oriented mobile layout: first and third columns share the top row,
  // while the middle column (typically search) spans the full row below. It is
  // opt-in and keeps arbitrary desktop composition untouched.
  const compactHeaderRules = compactHeader
    ? `.${rowClass} > .contents > [data-block-id]:nth-child(1) { grid-column: 1; order: 1 !important; }
.${rowClass} > .contents > [data-block-id]:nth-child(2) { grid-column: 1 / -1; order: 3 !important; }
.${rowClass} > .contents > [data-block-id]:nth-child(3) { grid-column: 2; order: 2 !important; }`
    : '';
  const compactHeaderCSS = compactHeaderRules
    ? isAdmin && viewport === 'mobile'
      ? compactHeaderRules
      : `@media (max-width: 767px) { .${rowClass} { grid-template-columns: minmax(0, 1fr) auto !important; } ${compactHeaderRules} }`
    : '';

  const mobileHeaderTemplate = centeredMobileHeader
    ? 'minmax(0, 1fr) auto minmax(0, 1fr)'
    : splitMobileHeader
      ? 'minmax(0, 1fr) auto auto'
      : '';
  const mobileHeaderTemplateCSS = mobileHeaderTemplate
    ? isAdmin && viewport === 'mobile'
      ? ''
      : `@media (max-width: 767px) { .${rowClass} { grid-template-columns: ${mobileHeaderTemplate} !important; } }`
    : '';

  // mobileOrder should only affect mobile layout.
  // Admin: pass orderOverrides only when simulating mobile viewport.
  // Storefront: inject a <style> tag scoped to @media (max-width: 767px) instead of inline styles.
  const hasMobileOrder = rowChildren.length > 0 && Object.keys(mobileOrder).length > 0;

  const adminOrderOverrides: Record<string, number> | undefined =
    isAdmin && viewport === 'mobile' && hasMobileOrder
      ? Object.fromEntries(
          rowChildren.map((child: any, i: number) => [
            child.id,
            mobileOrder[i] !== undefined ? mobileOrder[i] : i,
          ])
        )
      : undefined;

  const storefrontOrderCSS: string =
    !isAdmin && hasMobileOrder
      ? rowChildren
          .map((child: any, i: number) => {
            const order = mobileOrder[i] !== undefined ? mobileOrder[i] : i;
            return `.${rowClass} > .contents > [data-block-id="${child.id}"] { order: ${order}; }`;
          })
          .join(' ')
      : '';

  const rawAlign = currentStyles?.alignItems || block.settings?.alignItems || 'stretch';
  const rawJustify = currentStyles?.justifyContent || block.settings?.justifyContent || 'start';

  const alignItemsMap: Record<string, string> = { top: 'start', center: 'center', bottom: 'end', stretch: 'stretch' };
  const justifyContentMap: Record<string, string> = { start: 'start', center: 'center', end: 'end', between: 'space-between', around: 'space-around' };
  // No `start` entry on purpose: start is also the default for every row that never set an
  // alignment, and `justify-items: start` shrinks each item to its content width — blocks with no
  // intrinsic width (product gallery, fill images, maps, embeds) collapsed to 0px, so product pages
  // showed no photos. A stretched item already keeps its content at the start edge.
  const justifyItemsMap: Record<string, string> = { center: 'center', end: 'end' };

  // The visual background/min-height belongs to BlockRenderer's outer wrapper.
  // A percentage height on its inner wrapper cannot resolve against `min-height`,
  // so the grid used to stay only as tall as its content. In that situation
  // align-items had no spare vertical space and Top/Center/Bottom looked identical.
  // Give the actual grid the same responsive minimum height so those controls
  // operate on the box the merchant can see.
  const configuredMinHeight = currentStyles?.minHeight;
  const paddingTop = Number(currentStyles?.paddingTop ?? currentStyles?.paddingVertical ?? 0);
  const paddingBottom = Number(currentStyles?.paddingBottom ?? currentStyles?.paddingVertical ?? 0);
  const numericMinHeight = Number(configuredMinHeight);
  const rowMinHeight = configuredMinHeight === undefined || configuredMinHeight === null || configuredMinHeight === ''
    ? undefined
    : Number.isFinite(numericMinHeight)
      ? `${Math.max(0, numericMinHeight - (Number.isFinite(paddingTop) ? paddingTop : 0) - (Number.isFinite(paddingBottom) ? paddingBottom : 0))}px`
      : configuredMinHeight;

  return (
    <div
      className={cn(
        'grid w-full',
        rowClass,
        storefrontGridClass,
        !block.children?.length && 'min-h-[120px]'
      )}
      style={{
        // styles.gap is responsive (per viewport); settings.gap is the older,
        // single-value field kept as a fallback for rows saved before this changed.
        gap: `${currentStyles?.gap ?? block.settings?.gap ?? 32}px`,
        alignItems: alignItemsMap[rawAlign] || rawAlign,
        justifyContent: justifyContentMap[rawJustify] || rawJustify,
        // start/center/end should also position the grid items inside their
        // columns. Previously only the column tracks were aligned; `1fr`
        // tracks fill the entire row, so the horizontal buttons appeared to do
        // nothing. between/around intentionally keep the default stretch.
        justifyItems: justifyItemsMap[rawJustify],
        minHeight: rowMinHeight,
        ...(isAdmin ? { gridTemplateColumns: adminGridTemplateColumns } : {}),
      }}
    >
      {storefrontOrderCSS && (
        <style>{`@media (max-width: 767px) { ${storefrontOrderCSS} }`}</style>
      )}
      {storefrontWidthCSS && <style>{storefrontWidthCSS}</style>}
      {compactHeaderCSS && <style>{compactHeaderCSS}</style>}
      {mobileHeaderTemplateCSS && <style>{mobileHeaderTemplateCSS}</style>}
      {rowChildren.length === 0 ? (
        Array.from({ length: configuredColumns }).map((_, i) => (
          <div
            key={i}
            onClick={(e) => {
              if (isAdmin && (window as any).__prepareAdd) {
                e.stopPropagation();
                (window as any).__prepareAdd(block.id);
              }
            }}
            className="min-h-[120px] border-2 border-dashed border-slate-300/80 hover:border-blue-400 rounded-xl flex flex-col items-center justify-center text-slate-400 hover:text-blue-600 bg-slate-50/50 hover:bg-blue-50/20 text-[10px] font-bold uppercase tracking-widest transition-all cursor-pointer group/add-slot select-none"
          >
            <PlusCircle className="w-5 h-5 mb-1.5 opacity-40 group-hover/add-slot:opacity-100 transition-opacity" />
            Kolumna {i + 1} – Dodaj
          </div>
        ))
      ) : (
        <ImageSlotContext.Provider value={columnImageSlot}>
        <BlockRenderer
          blocks={rowChildren}
          viewport={viewport}
          product={product}
          category={category}
          selectedBlockId={selectedBlockId}
          onSelectBlock={onSelectBlock}
          onUpdateBlockStyle={onUpdateBlockStyle}
          onUpdateBlockContent={onUpdateBlockContent}
          isAdmin={isAdmin}
          isBuilderCanvas={isBuilderCanvas}
          isNested={true}
          orderOverrides={adminOrderOverrides}
          depth={depth + 1}
          onDuplicateBlock={onDuplicateBlock}
          onMoveBlockUp={onMoveBlockUp}
          onMoveBlockDown={onMoveBlockDown}
          onRemoveBlock={onRemoveBlock}
          initialBlockData={initialBlockData}
          authMode={authMode}
          prioritizeFirstVisible={isFirstBlock ? (firstBlockBreakpoints ?? true) : false}
          // A hero row's image usually sits in the 2nd column (text first) — it is the mobile
          // LCP element, yet only the 1st column used to get eager/high-priority loading.
          prioritizeAllVisible={isFirstBlock}
        />
        </ImageSlotContext.Provider>
      )}
    </div>
  );
}
